import React from "react";
import "./signin.css";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signinUser } from "../../../services/authAPI";
import { loginSuccess } from "../../../redux/actions/authActions";

interface Credentials {
  email: string;
  password: string;
}

interface SignInProps {
  credentials: Credentials;
  setCredentials: React.Dispatch<React.SetStateAction<Credentials>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  success: boolean;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setLoginOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDialogView: React.Dispatch<React.SetStateAction<"signin" | "forgot">>;
}

export default function SignIn({
  credentials,
  setCredentials,
  loading,
  setLoading,
  error,
  setError,
  success,
  setSuccess,
  setLoginOpen,
  setDialogView,
}: SignInProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    if (!credentials.email.trim()) {
      setError("email");
      setLoading(false);
      return;
    }
    if (!credentials.password) {
      setError("password");
      setLoading(false);
      return;
    }

    try {
      const response = await signinUser(credentials);

      if (response.accessToken) {
        localStorage.setItem("accessToken", response.accessToken);
      }
      if (response.refreshToken) {
        localStorage.setItem("refreshToken", response.refreshToken);
      }

      // ── Redux: store authenticated user details ──
      dispatch(
        loginSuccess({
          user: response.user, // 👈 FULL USER OBJECT
          accessToken: response.accessToken ?? "",
          refreshToken: response.refreshToken ?? "",
        }),
      );

      setSuccess(true);
      setTimeout(() => {
        setLoading(false);
        setLoginOpen(false);
        setCredentials({ email: "", password: "" });
        navigate("/dashboard"); // ── navigate to matrimony list ──
      }, 1000);
    } catch (err: any) {
      setError(err.message || "Invalid email or password");
      setLoading(false);
    }
  };

  // Derive per-field error messages
  const emailError =
    error === "email" ? "Email is required" : error && !credentials.email.trim() ? "Email is required" : null;

  const passwordError =
    error === "password" ? "Password is required" : error && !credentials.password ? "Password is required" : null;

  // General API error (not a field-specific string)
  const generalError = error && error !== "email" && error !== "password" ? error : null;

  return (
    <div className='page-wrapper'>
      <main className='main-content'>
        <div className='login-card'>
          {/* Left Panel */}
          <div className='left-panel'>
            <div className='left-panel-content'>
              <h2 className='welcome-title'>Welcome Back</h2>
              <p className='welcome-subtitle'>
                Login to find your perfect life partner from Sri Lanka's trusted matrimony.
              </p>
            </div>
          </div>

          {/* Right Panel */}
          <div className='right-panel'>
            <h1 className='login-heading'>Log In</h1>

            <div className='login-form'>
              {/* Email */}
              <div className='form-group'>
                <label className='form-label'>Email</label>
                <input
                  type='text'
                  name='email'
                  className={`form-input${emailError ? " input-error" : ""}`}
                  placeholder='Enter email'
                  value={credentials.email}
                  onChange={handleChange}
                  disabled={loading}
                />
                {emailError && <span className='field-error-msg'>{emailError}</span>}
              </div>

              {/* Password */}
              <div className='form-group'>
                <label className='form-label'>Password</label>
                <input
                  type='password'
                  name='password'
                  className={`form-input${passwordError ? " input-error" : ""}`}
                  placeholder='Enter password'
                  value={credentials.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                {passwordError && <span className='field-error-msg'>{passwordError}</span>}
                <div className='forgot-password'>
                  <a
                    href='#'
                    className='forgot-link'
                    onClick={(e) => {
                      e.preventDefault();
                      setDialogView("forgot");
                    }}
                  >
                    🔑 Forgot Password?
                  </a>
                </div>
              </div>

              {/* General API error */}
              {generalError && <p className='error-message'>{generalError}</p>}

              {/* Success message */}
              {success && <p className='success-message'>✅ Login successful!</p>}

              {/* Submit */}
              <button className={`login-btn${loading ? " loading" : ""}`} onClick={handleSubmit} disabled={loading}>
                {loading ? <span className='spinner' /> : <>Log In</>}
              </button>

              <p className='register-prompt'>
                Don't have an account?{" "}
                <a
                  href='#'
                  className='register-link'
                  onClick={(e) => {
                    e.preventDefault();
                    setLoginOpen(false);
                    navigate("/register");
                  }}
                >
                  Register
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
